import { useParams } from "react-router-dom";
import { useFetch } from "../lib/useFetch";
import { PageHeader, Panel, Stat, Loading, Chip } from "../components/common/ui";
import { SubTabs } from "../components/common/SubTabs";
import { MultiLine, AreaTrend } from "../components/common/Charts";
import { inr } from "../lib/format";
import { cn } from "../lib/utils";
import { AlertTriangle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Progress } from "../components/ui/progress";

const TABS = [
  { key: "production", label: "Production" }, { key: "machines", label: "Machines" },
  { key: "capacity", label: "Capacity" }, { key: "chemicals", label: "Chemicals" },
  { key: "quality", label: "Factory Quality" },
];

export default function FactoryIntelligence() {
  const { tab = "production" } = useParams();
  return (
    <div data-testid="factory-page">
      <PageHeader title="Factory Intelligence" subtitle="Bengaluru central factory — production, machines, capacity and chemical intelligence." />
      <SubTabs base="/factory" tab={tab} items={TABS} />
      {tab === "production" && <Production />}
      {tab === "machines" && <Machines />}
      {tab === "capacity" && <Capacity />}
      {tab === "chemicals" && <Chemicals />}
      {tab === "quality" && <Quality />}
    </div>
  );
}

function Production() {
  const { data, loading } = useFetch("/factory/production", []);
  if (loading || !data) return <Loading rows={4} />;
  const t = data.totals;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Stat label="Incoming KG (30d)" value={t.incoming_kg.toLocaleString("en-IN")} />
        <Stat label="Processed KG" value={t.processed_kg.toLocaleString("en-IN")} tone="text-primary" />
        <Stat label="Pending KG" value={t.pending_kg.toLocaleString("en-IN")} tone="text-amber-600" />
        <Stat label="Batches" value={t.batch_count.toLocaleString("en-IN")} />
        <Stat label="Rewash %" value={`${t.rewash_pct}%`} />
        <Stat label="Rejection %" value={`${t.rejection_pct}%`} />
        <Stat label="Proc. Time" value={`${t.processing_time_min}m`} />
      </div>
      <Panel title="Incoming vs Processed (KG)">
        <MultiLine data={data.trend} lines={[{ key: "incoming", name: "Incoming" }, { key: "processed", name: "Processed" }]} height={320} />
      </Panel>
    </div>
  );
}

function Machines() {
  const { data, loading } = useFetch("/factory/machines", []);
  if (loading || !data) return <Loading rows={5} />;
  const badge = (s) => ({
    "over-utilized": "bg-red-100 text-red-700", "abnormal-downtime": "bg-amber-100 text-amber-700",
    "maintenance-due": "bg-amber-100 text-amber-700", healthy: "bg-emerald-100 text-emerald-700",
  }[s] || "bg-slate-100 text-slate-600");
  return (
    <Panel title="Machine Intelligence" subtitle="Utilisation, downtime, errors and maintenance">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Machine</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Cap</TableHead>
            <TableHead className="text-right">Util</TableHead><TableHead className="text-right">Runtime</TableHead>
            <TableHead className="text-right">Downtime</TableHead><TableHead className="text-right">Production</TableHead>
            <TableHead className="text-right">Errors</TableHead><TableHead>Next Maint.</TableHead><TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((m) => (
            <TableRow key={m.id} className="hover:bg-slate-50">
              <TableCell className="font-medium">{m.code}</TableCell>
              <TableCell className="text-slate-500">{m.type}</TableCell>
              <TableCell className="text-right">{m.capacity_kg}kg</TableCell>
              <TableCell className={cn("text-right font-medium", m.utilization > 95 ? "text-red-600" : m.utilization < 55 ? "text-amber-600" : "")}>{m.utilization}%</TableCell>
              <TableCell className="text-right">{m.runtime_hr}h</TableCell>
              <TableCell className={cn("text-right", m.downtime_hr > 15 ? "text-red-600 font-medium" : "")}>{m.downtime_hr}h</TableCell>
              <TableCell className="text-right">{m.production_kg}kg</TableCell>
              <TableCell className="text-right">{m.errors}</TableCell>
              <TableCell className="text-slate-500">{m.next_maintenance}</TableCell>
              <TableCell><Chip className={badge(m.status)}>{m.status}</Chip></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

function Capacity() {
  const { data, loading } = useFetch("/factory/capacity", []);
  if (loading || !data) return <Loading rows={3} />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat label="Daily Capacity" value={`${data.daily_capacity.toLocaleString("en-IN")} kg`} />
        <Stat label="Current Load" value={`${data.current_load.toLocaleString("en-IN")} kg`} tone="text-primary" />
        <Stat label="Available" value={`${data.available.toLocaleString("en-IN")} kg`} />
        <Stat label="Utilisation" value={`${data.utilization}%`} tone={data.utilization > 90 ? "text-red-600" : ""} />
        <Stat label="Backlog" value={`${data.backlog.toLocaleString("en-IN")} kg`} tone="text-amber-600" />
      </div>
      <Panel title="Utilisation">
        <Progress value={data.utilization} className="h-3" />
        <div className="flex justify-between text-xs text-slate-400 mt-1"><span>Peak hours {data.peak_hours}</span><span>{data.utilization}% of capacity</span></div>
      </Panel>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div><div className="text-sm font-semibold text-amber-800">Bottleneck detected</div><p className="text-sm text-amber-700 mt-0.5">{data.bottleneck}</p></div>
      </div>
      <Panel title="Utilisation Trend (30d)"><AreaTrend data={data.trend.map((d) => ({ date: d.date, value: d.utilization }))} color="#E4A11B" height={260} /></Panel>
    </div>
  );
}

function Chemicals() {
  const { data, loading } = useFetch("/factory/chemicals", []);
  if (loading || !data) return <Loading rows={4} />;
  const anomaly = data.find((c) => c.status === "anomaly");
  return (
    <div className="space-y-6">
      {anomaly && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-red-800">{anomaly.name} consumption is {anomaly.diff_pct}% above baseline while production increased only {anomaly.production_growth_pct}%.</div>
            <p className="text-sm text-red-700 mt-0.5">Estimated excess cost ≈ ₹{inr(anomaly.excess_cost)}. Recommended action: recalibrate dosing and audit the {anomaly.process} process.</p>
          </div>
        </div>
      )}
      <Panel title="Chemical Intelligence" subtitle="Dosage, consumption vs baseline and excess cost">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Chemical</TableHead><TableHead>Machine</TableHead><TableHead>Process</TableHead>
              <TableHead className="text-right">KG Processed</TableHead><TableHead className="text-right">Rec. Dosage</TableHead>
              <TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Consumption</TableHead>
              <TableHead className="text-right">Baseline</TableHead><TableHead className="text-right">Diff</TableHead>
              <TableHead className="text-right">Excess Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((c) => (
              <TableRow key={c.id} className={cn("hover:bg-slate-50", c.status === "anomaly" && "bg-red-50/40")}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-slate-500">{c.machine}</TableCell>
                <TableCell className="text-slate-500">{c.process}</TableCell>
                <TableCell className="text-right">{c.kg_processed.toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-right">{c.recommended_dosage}ml</TableCell>
                <TableCell className="text-right">{c.actual_dosage}ml</TableCell>
                <TableCell className="text-right">{c.consumption_l}L</TableCell>
                <TableCell className="text-right">{c.baseline_l}L</TableCell>
                <TableCell className={cn("text-right font-medium", c.diff_pct > 8 ? "text-red-600" : c.diff_pct < 0 ? "text-emerald-600" : "")}>{c.diff_pct}%</TableCell>
                <TableCell className="text-right">₹{inr(c.excess_cost)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
    </div>
  );
}

function Quality() {
  const { data, loading } = useFetch("/factory/quality", []);
  if (loading || !data) return <Loading rows={3} />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Quality Score" value={`${data.quality_score}%`} tone="text-primary" />
        <Stat label="Rewash Rate" value={`${data.rewash_pct}%`} />
        <Stat label="Rejection Rate" value={`${data.rejection_pct}%`} />
        <Stat label="Inspection Pass" value={`${data.inspections_passed}%`} tone="text-emerald-600" />
      </div>
      <Panel title="Rewash & Rejection Trend"><MultiLine data={data.trend} lines={[{ key: "rewash", name: "Rewash %" }, { key: "rejection", name: "Rejection %" }]} height={300} /></Panel>
    </div>
  );
}
